import React, { useState, useEffect } from 'react';
import {
  Sparkles, MessageCircle, ShieldCheck, Coffee, Zap, Clock, MessagesSquare,
} from 'lucide-react';

/* Échantillon d'échanges joués en boucle sur la page d'accueil. Le but n'est pas
   de décrire le produit mais de le faire entendre : c'est la voix d'Olivier qui
   doit convaincre, pas l'argumentaire. */
const DEMO = [
  {
    q: 'Je fais quoi de mon dimanche ?',
    a: "Tu commences par un vrai café, pas la capsule oubliée au fond du placard. Ensuite tu sors marcher. Le reste s'improvise — c'est un peu le principe du dimanche.",
  },
  {
    q: 'Chocolatine ou pain au chocolat ?',
    a: "Chocolatine. Ce n'est pas un débat, c'est un test de personnalité. Et tu viens de le rater rien qu'en hésitant.",
  },
  {
    q: "J'ai un entretien demain, un conseil ?",
    a: "Arrive dix minutes en avance, garde une question pour la fin, et ne dis surtout pas que tu es « perfectionniste ». Personne n'y croit. Moi le premier.",
  },
  {
    q: 'Tu sais faire un barbecue, toi ?',
    a: "Alors. Techniquement, oui. Statistiquement, on préfère confier ça à quelqu'un d'autre. Sujet suivant.",
  },
];

const SPECS = [
  { icon: <Clock size={15} />, value: '24/7', label: 'disponible, enfin' },
  { icon: <Zap size={15} />, value: '2 s', label: 'de temps de réflexion' },
  { icon: <MessagesSquare size={15} />, value: '∞', label: 'avis non sollicités' },
  { icon: <Coffee size={15} />, value: '1', label: 'sujet dont il ne se lasse pas' },
];

export default function Landing({ setScreen, userAuthed }) {
  const enter = () => setScreen(userAuthed ? 'chat' : 'gate');

  return (
    <div className="page">
      <div className="hero">
        <div className="hero-glow" />
        {/* Fond clair : c'est la version sombre du logo qui s'impose ici. */}
        <div className="hero-logo"><img src="/logo.svg" alt="Oliv'IA" /></div>
        <span className="eyebrow"><Sparkles size={13} /> Une IA d'exception, un modèle unique</span>
        <h1 className="h1">
          L'intelligence d'<span className="grad-text">Olivier</span>,<br />enfin disponible 24/7.
        </h1>
        <p className="lead">
          Le bon conseil, la répartie, l'avis tranché qu'il fallait : Olivier a toujours été là pour ça.
          Oliv'IA raisonne comme lui, parle comme lui — et ne se dérobe jamais.
        </p>
        <div className="hero-actions">
          <button className="btn-primary big" onClick={enter}>
            <MessageCircle size={17} /> Consulter Oliv'IA
          </button>
        </div>
        <div className="specs">
          {SPECS.map((s) => (
            <div key={s.label} className="spec">
              <span className="spec-ico">{s.icon}</span>
              <b>{s.value}</b>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <DemoChat onEnter={enter} />

      <div className="why">
        <div className="why-card">
          <div className="why-tag">Pourquoi cette IA ?</div>
          <p className="why-text">
            Désormais <b>totalement dédié à Marie-Alix</b>, Olivier n'a tout simplement plus la disponibilité
            d'antan. Plutôt que de vous laisser sans son avis éclairé, il a pris la seule décision raisonnable :
            <b> se dupliquer</b>. Oliv'IA vous conseille au mieux, comme il l'a toujours fait au quotidien —
            l'agenda saturé en moins.
          </p>
        </div>
      </div>

      <div className="cards-3">
        <InfoCard
          icon={<Sparkles size={20} />}
          title="Il ne récite rien"
          desc="Posez-lui une question que personne ne lui a jamais posée : il répond quand même. Pas de réponse pré-écrite, pas de « je ne sais pas » — il extrapole son propre caractère, comme il le ferait en vrai."
        />
        <InfoCard
          icon={<MessageCircle size={20} />}
          title="Il a un avis sur tout"
          desc="Tranché, argumenté, parfois de mauvaise foi assumée. Il conseille, il taquine, il se moque un peu de lui-même — et il ne sort jamais du personnage."
          featured
        />
        <InfoCard
          icon={<ShieldCheck size={20} />}
          title="Il reste entre nous"
          desc="Accès réservé à ses proches, par code authenticator ou coupon provisoire. Les conversations sont effacées après la soirée."
        />
      </div>
    </div>
  );
}

/* Démonstration : une question apparaît, Olivier « réfléchit », puis répond.
   L'animation s'efface pour qui a demandé à réduire les animations. */
function DemoChat({ onEnter }) {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('typing');
  const reduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced) {
      setPhase('answer');
      return undefined;
    }
    const delay = phase === 'typing' ? 1400 : 5200;
    const timer = setTimeout(() => {
      if (phase === 'typing') {
        setPhase('answer');
      } else {
        setIndex((i) => (i + 1) % DEMO.length);
        setPhase('typing');
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [phase, reduced]);

  const current = DEMO[index];

  return (
    <div className="demo">
      <div className="demo-head">
        <span className="demo-tag"><span className="live-dot" /> Un aperçu, en vrai</span>
        <p className="demo-sub">Ce sont ses réponses, son ton, ses convictions. Sans filtre et sans script.</p>
      </div>

      <div className="demo-shell">
        <div className="msg user">
          <div className="bubble" key={`q${index}`}>{current.q}</div>
        </div>
        <div className="msg assistant">
          <div className="msg-ava"><img src="/logo.svg" alt="" /></div>
          {phase === 'typing' && !reduced
            ? <div className="bubble typing"><i /><i /><i /></div>
            : <div className="bubble" key={`a${index}`}>{current.a}</div>}
        </div>

        <div className="demo-dots">
          {DEMO.map((d, i) => (
            <button
              key={d.q}
              className={`demo-dot${i === index ? ' on' : ''}`}
              onClick={() => { setIndex(i); setPhase('typing'); }}
              aria-label={`Question ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <button className="demo-cta" onClick={onEnter}>
        À votre tour de lui demander <MessageCircle size={15} />
      </button>
    </div>
  );
}

function InfoCard({ icon, title, desc, featured }) {
  return (
    <div className={`flow-card static${featured ? ' featured' : ''}`}>
      <div className="fc-icon">{icon}</div>
      <h3 className="fc-title">{title}</h3>
      <p className="fc-desc">{desc}</p>
    </div>
  );
}
